import React, { ChangeEvent, FC, PropsWithChildren, useEffect, useState } from 'react'
import { Button, Box, CircularProgress, MenuItem, Menu } from '@mui/material'
import { PropsInjector, DataLoad } from 'src/components'
import { useAddPopupMessage, useDialog, useQueryParams, useQueryParamsState } from 'src/hooks/ui'
import { DialogNames, SearchParamsKeys, ViewTypes, XlsxAllowedTypes } from 'src/enums'
import { GridActionsCellItem, GridColDef } from '@mui/x-data-grid-premium'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import { useVerizonReportsListRequest, useNewVerizonReportRequest, useDeleteVerizonReportRequest, useUploadFileRequest } from 'src/hooks/api'
import { VerizonReport } from 'src/api'
import { VerizonReportsColumns } from 'src/components/columns/sensor'
import { CommonSensorList } from './commonSensorsList'
import { DeleteVerizonReportModal } from '../deleteVerizonReportModal'

const allowedTypes = [XlsxAllowedTypes.Xls, XlsxAllowedTypes.Xlsx]

interface VerizonReportsListProps {
  data?: VerizonReport[]
  count?: number
  isLoading?: boolean
  refetchVerizonReports?: () => void
}

const SensorsList: FC<VerizonReportsListProps> = ({ data = [], count = 0, refetchVerizonReports = () => {} }) => {
  const [currentRow, setCurrentRow] = useState<VerizonReport | null>(null)
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null)

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>, row: VerizonReport) => {
    setCurrentRow(row)
    setMenuAnchorEl(event.currentTarget)
  }

  const handleCloseMenu = () => {
    setCurrentRow(null)
    setMenuAnchorEl(null)
  }

  const getColumns = (): GridColDef<VerizonReport>[] => [
    VerizonReportsColumns.Id,
    VerizonReportsColumns.CreatedAt,
    VerizonReportsColumns.VerizonAttachmentFileName,
    VerizonReportsColumns.ResultAttachmentFileName,
    {
      field: 'actions',
      headerName: '',
      type: 'actions',
      width: 30,
      getActions: ({ row }) => [
        <GridActionsCellItem icon={<MoreVertIcon />} label="Actions" className="textPrimary" onClick={(e: any) => handleOpenMenu(e, row)} color="inherit" />
      ]
    }
  ]

  const addMessage = useAddPopupMessage()

  const createVerizonReportRequest = useNewVerizonReportRequest()

  const createVerizonReport = (attachmentId: number): Promise<VerizonReport | null> => {
    return new Promise((resolve) => {
      createVerizonReportRequest.mutate(
        {
          verizonAttachmentId: attachmentId
        },
        {
          onSuccess: (result: any) => {
            resolve(result)
          },
          onError: (error: any) => {
            addMessage({ text: error.message, type: 'error' })
            resolve(null)
          }
        }
      )
    })
  }

  const deleteVerizonReportRequest = useDeleteVerizonReportRequest()

  const { isLoading, mutate } = useUploadFileRequest({
    options: {
      onSuccess: async (uploadResult) => {
        await createVerizonReport(parseInt(uploadResult[0].id))
        refetchVerizonReports()
      },
      onError: ({ message: text }) => {
        addMessage({ text, type: 'error' })
      }
    }
  })

  const onFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const { files } = event.target
    if (!files || files?.length === 0) return
    const [file] = Array.from(files)
    const isTypeAllowed = allowedTypes.some((type) => file.type === type)
    if (isTypeAllowed) {
      mutate({ files: [file] })
    } else addMessage({ text: 'Incorrect file type', type: 'error' })
  }

  const { openDialog: openConfirmDeleteDialog, closeDialog: closeConfirmDeleteDialog } = useDialog(DialogNames.DeleteVerizonReport)

  return (
    <>
      <Box display="flex" justifyContent="flex-end" py={2}>
        <Button
          component="label"
          size="large"
          variant="contained"
          color="primary"
          endIcon={isLoading ? <CircularProgress size={15} sx={{ color: 'white' }} /> : null}
          disabled={isLoading}
        >
          Upload Verizon Report
          {!isLoading && <input type="file" hidden onChange={onFileChange} />}
        </Button>
      </Box>

      <CommonSensorList columns={getColumns()} count={count} data={data} isLoading={isLoading} hideToolbar={true} />
      <Menu anchorEl={menuAnchorEl} open={!!menuAnchorEl} onClose={handleCloseMenu}>
        <MenuItem disabled={isLoading || !currentRow?.id} onClick={openConfirmDeleteDialog}>
          Delete
        </MenuItem>
      </Menu>
      <DeleteVerizonReportModal
        isLoading={isLoading}
        onCancel={() => {
          closeConfirmDeleteDialog()
          handleCloseMenu()
        }}
        onConfirm={() => {
          deleteVerizonReportRequest.mutate(
            {
              id: currentRow!.id
            },
            {
              onSuccess: () => {
                refetchVerizonReports()
                closeConfirmDeleteDialog()
                handleCloseMenu()
              }
            }
          )
        }}
      />
    </>
  )
}

const VerizonReportsListLoader: FC<PropsWithChildren> = ({ children }) => {
  const { isAvailableInSystem, ...state } = useQueryParamsState()

  const { data, isInitialLoading, error, isError, refetch } = useVerizonReportsListRequest({
    params: {
      ...state
    }
  })

  const props = {
    data: data?.rows || [],
    count: data?.count || 0,
    isLoading: isInitialLoading,
    refetchVerizonReports: refetch
  }

  return (
    <DataLoad isLoading={false} isError={isError} errorMessage={error?.message}>
      <PropsInjector props={props}>{children}</PropsInjector>
    </DataLoad>
  )
}

export const VerizonReportsList: FC = () => {
  const [params, setParams] = useQueryParams()
  useEffect(() => {
    setParams({
      ...params,
      [SearchParamsKeys.ViewType]: ViewTypes.List
    })
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <VerizonReportsListLoader>
      <SensorsList />
    </VerizonReportsListLoader>
  )
}
